import {forwardRef, useEffect, useImperativeHandle, useState} from 'react'
import AddAction from './actions/Add'
import {Pagination} from './Pagination'
import Table from './Table'
import {CrudProps, GridProps, GridRef, LoadFunctionArgs} from './types'
import Spinner from '../Spinner'
import {useSelector} from 'react-redux'
import {RootState} from 'setup'
import ExportCsv from './exports/ExportCsv'
import ExportPdf from './exports/ExportPdf'
import moment from 'moment'
import BulkActions from './bulkActions'
import ExtraActions from './extraActions'
import {DatePicker} from '../Fields'

const Grid = forwardRef<GridRef | undefined | null, GridProps>((props, ref) => {
  const {
    columns,
    data,
    crud,
    onLoad,
    bulkActions,
    maxExtraActions,
    extraActions,
    pageSize = undefined,
    currentPage = 1,
    totalPages = 1,
    totalRecords = 1,
    title,
    loading = false,
    customComponent,
    exportCsv = false,
    csvFilename = 'csv_data',
    csvTitle,
    exportPdf = false,
    pdfFilename = 'pdf_data',
    pdfTitle,
    dateRangeFilter,
    defaultRange,
    pagination = true,
    autoSelectRows = [],
  } = props

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as any

  const [crudOperation, setCrud] = useState<CrudProps>()

  const [search, setSearch] = useState({})

  const [sorting, setSorting] = useState<Record<string, 'asc' | 'desc'>>({id: 'desc'})

  const [page, setPage] = useState(currentPage)
  const [itemPerPage, setItemPerPage] = useState(pageSize ?? generalSettings?.default_page_size)

  const [dateRange, setDateRange] = useState<[Date | string, Date | string]>(
    defaultRange || ['', '']
  )

  const [selectedRows, setSelectedRows] = useState<any[]>([])

  useEffect(() => {
    crud ? setCrud(crud) : setCrud({canAdd: '/', canEdit: '/', canDelete: true, canRead: true})
    const params: LoadFunctionArgs = {
      page,
      pageSize: itemPerPage,
      sort: sorting,
      search,
    }

    if (dateRange[0] && dateRange[1]) {
      params.dateRange = {
        startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
        endDate: moment(dateRange[1]).format('YYYY-MM-DD'),
      }
    }

    onLoad(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemPerPage, search, sorting, dateRange])

  useEffect(() => {
    if (autoSelectRows.length > 0) {
      const rowsToSelect = data.filter((row) => autoSelectRows.includes(row.id))
      if (
        rowsToSelect.length !== selectedRows.length ||
        !rowsToSelect.every((row) => selectedRows.some((selected) => selected.id === row.id))
      ) {
        setSelectedRows(rowsToSelect)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectRows, data])

  useImperativeHandle(
    ref,
    () => ({
      reload: () => {
        const params: LoadFunctionArgs = {
          page,
          pageSize: itemPerPage,
          sort: sorting,
          search,
          dateRange: {},
        }
        if (dateRange[0] && dateRange[1]) {
          params.dateRange = {
            startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
            endDate: moment(dateRange[1]).format('YYYY-MM-DD'),
          }
        }

        if (dateRange[0] && dateRange[1]) {
          params.dateRange = {
            startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
            endDate: moment(dateRange[1]).format('YYYY-MM-DD'),
          }
        }

        setSelectedRows([])

        onLoad(params)
      },
    }),
    [page, itemPerPage, sorting, search, dateRange, onLoad]
  )

  const showHeader =
    bulkActions ||
    extraActions ||
    dateRangeFilter ||
    customComponent ||
    exportCsv ||
    exportPdf ||
    crudOperation?.canAdd
      ? true
      : false

  return (
    <div className='card card-custom'>
      {showHeader && (
        <div className='card-header px-1 px-md-2'>
          {/* {(!bulkActions || bulkActions.length === 0 || selectedRows.length === 0) && (
          <div className='card-title d-flex flex-fill flex-wrap'>
            {title && <h3 className='card-label'>{title}</h3>}
            {extraActions && extraActions.length > 0 && (
              <ExtraActions actions={extraActions} maxExtraActions={maxExtraActions} />
            )}
          </div>
        )}
        {bulkActions && bulkActions.length > 0 && (
          <div className={clsx('card-toolbar', {'d-none': selectedRows.length === 0})}>
            <BulkActions actions={bulkActions} selectedRows={selectedRows} className='me-1' />
            {extraActions && extraActions.length > 0 && (
              <ExtraActions actions={extraActions} maxExtraActions={maxExtraActions} />
            )}
          </div>
        )} */}
          <div className='card-toolbar'>
            {bulkActions && bulkActions.length > 0 && (
              <BulkActions actions={bulkActions} selectedRows={selectedRows} className='me-1' />
            )}
            {extraActions && extraActions.length > 0 && (
              <ExtraActions actions={extraActions} maxExtraActions={maxExtraActions} />
            )}
          </div>

          <div className='card-toolbar'>
            {dateRangeFilter && (
              <DatePicker
                groupClass='me-1'
                options={{
                  mode: 'range',
                  enableTime: false,
                }}
                value={dateRange?.length === 2 ? [dateRange[0], dateRange[1]] : ''}
                onChange={(update) => {
                  const [start, end] = update
                  setDateRange([start, end])
                }}
                isClearable
                onClear={() => setDateRange(['', ''])}
              />
            )}

            {customComponent}

            {exportCsv && (
              <ExportCsv columns={columns} data={data} filename={csvFilename} title={csvTitle} />
            )}
            {exportPdf && (
              <ExportPdf
                columns={columns}
                data={data}
                filename={pdfFilename}
                title={pdfTitle || title}
              />
            )}

            {crudOperation?.canAdd && (
              <AddAction url={crudOperation.canAdd} title={crud.addTitle || 'GENERAL.ADD'} />
            )}
          </div>
        </div>
      )}
      <div className='card-body position-relative px-1'>
        {loading && (
          <div
            className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
            style={{backgroundColor: 'rgb(255, 255, 255, .5)', zIndex: '50'}}
          >
            <Spinner color='primary' type='grow' />
          </div>
        )}
        <div className='table-responsive'>
          <Table
            columns={columns}
            data={data}
            canRead={crudOperation?.canRead}
            canEdit={crudOperation?.canEdit}
            canDelete={crudOperation?.canDelete}
            pageSize={itemPerPage}
            currentPage={page}
            onSorting={(field: string, order: 'asc' | 'desc') => {
              const obj: Record<string, 'asc' | 'desc'> = {}
              obj[field] = order
              setSorting(obj)

              // setFilter(field, order, search)
            }}
            onSearch={(value: any) => {
              setSearch(value)
              setPage(1)
              // setFilter(sorting.field, value)
            }}
            hasBulkActions={bulkActions && bulkActions.length > 0}
            selectedRows={selectedRows}
            setSelectedRows={(rows: any[]) => setSelectedRows(rows)}
          />
        </div>
      </div>
      {pagination && (
        <div className='card-footer px-1'>
          <Pagination
            totalPages={totalPages}
            pageSize={itemPerPage}
            currentPage={page}
            totalRecords={totalRecords}
            onPageChange={(currentPage: number, pageSize: number) => {
              // setFilter(sorting.field, sorting.order, search)
              setPage(currentPage)
              setItemPerPage(pageSize)
            }}
          />
        </div>
      )}
    </div>
  )
})

export default Grid
